import type {WebCommitInfo} from '@github-ui/code-view-types'
import {Banner, Button, ConfirmationDialog, Dialog, IconButton, Flash, Spinner} from '@primer/react'
import {ArrowLeftIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {Suspense, lazy, useCallback, useRef, useState} from 'react'

import styles from './TriageAgentConfigModal.module.css'
import type {CanCommitStatus} from './use-triage-agent-config'
import {useTriageAgentConfig, useSaveTriageAgentConfig} from './use-triage-agent-config'
import {
  parseConfigContent,
  serializeConfigContent,
  getDefaultFormState,
  isFormStateEqual,
  extractMarkdownBody,
  type TriageConfigFormState,
} from './config-parser'

import type {WebCommitDialogState} from '@github-ui/web-commit-dialog'
import {ActionsSection} from './components/ActionsSection'
import {PromptSection} from './components/PromptSection'
import {TriggersSection} from './components/TriggersSection'
import {getTriageAgentConfigUrl} from './utils/triage-agent-urls'

// Lazy load WebCommitDialog for SSR compatibility
const WebCommitDialog = lazy(async () => {
  const module = await import('@github-ui/web-commit-dialog')
  return {default: module.WebCommitDialog}
})

type TriageAgentConfigModalProps = {
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
  repoOwner: string
  repoName: string
}

function buildWebCommitInfo(
  canCommitStatus: CanCommitStatus,
  authorEmails: string[],
  defaultEmail: string,
  defaultNewBranchName: string,
  commitOid: string,
  dcoSignoffEnabled: boolean,
): WebCommitInfo {
  return {
    canCommitStatus,
    authorEmails,
    defaultEmail,
    defaultNewBranchName,
    commitOid,
    dcoSignoffEnabled,
    forkedRepo: undefined,
    lockedOnMigration: false,
    pr: '',
    repoHeadEmpty: false,
    saveUrl: '',
    shouldFork: false,
    shouldUpdate: false,
    suggestionsUrlEmoji: '',
    suggestionsUrlIssue: '',
    suggestionsUrlMention: '',
  }
}

export function TriageAgentConfigModal({isOpen, onClose, onSaved, repoOwner, repoName}: TriageAgentConfigModalProps) {
  const {data: configData, isLoading, isError} = useTriageAgentConfig(repoOwner, repoName, isOpen)
  const {mutateAsync: saveConfig} = useSaveTriageAgentConfig(repoOwner, repoName)

  // Structured form state (null = not yet initialized from server data)
  const [formState, setFormState] = useState<TriageConfigFormState | null>(null)
  // Track whether the original config had invalid frontmatter
  const [hasInvalidConfig, setHasInvalidConfig] = useState(false)
  // Snapshot of the initial form state for dirty tracking
  const [initialFormState, setInitialFormState] = useState<TriageConfigFormState | null>(null)
  // Track whether we've already initialized from server data
  const [initialized, setInitialized] = useState(false)

  const [phase, setPhase] = useState<'edit' | 'commit'>('edit')
  const [showValidation, setShowValidation] = useState(false)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)

  // Commit dialog state
  const [dialogState, setDialogState] = useState<WebCommitDialogState>('closed')
  const [commitMessage, setCommitMessage] = useState('')
  const [commitDescription, setCommitDescription] = useState('')
  const [isQuickPull, setIsQuickPull] = useState(false)
  const [authorEmail, setAuthorEmail] = useState<string | undefined>(undefined)
  const [prTargetBranch, setPRTargetBranch] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [promptExpanded, setPromptExpanded] = useState(false)

  const returnFocusRef = useRef<HTMLButtonElement>(null)

  // Initialize form state from server data (runs once when data arrives)
  if (configData && !initialized) {
    const parseResult = parseConfigContent(configData.content)
    if (parseResult.ok) {
      setFormState(parseResult.state)
      setInitialFormState(parseResult.state)
    } else {
      const body = extractMarkdownBody(configData.content)
      const defaults = getDefaultFormState(body)
      setFormState(defaults)
      setInitialFormState(defaults)
      if (configData.exists) {
        setHasInvalidConfig(true)
      }
    }
    setInitialized(true)
  }

  // Derive content for saving
  const content = formState ? serializeConfigContent(formState) : ''

  const serverRequiresQuickPull = configData?.commit_info.can_commit_status !== 'allowed'
  const effectiveIsQuickPull = serverRequiresQuickPull || isQuickPull
  const effectivePRTargetBranch = prTargetBranch || configData?.commit_info.default_new_branch_name || ''

  const isDirty = formState !== null && initialFormState !== null && !isFormStateEqual(formState, initialFormState)

  const resetState = useCallback(() => {
    setFormState(null)
    setHasInvalidConfig(false)
    setInitialFormState(null)
    setInitialized(false)
    setPhase('edit')
    setShowValidation(false)
    setShowDiscardConfirm(false)
    setDialogState('closed')
    setCommitMessage('')
    setCommitDescription('')
    setIsQuickPull(false)
    setAuthorEmail(undefined)
    setPRTargetBranch('')
    setErrorMessage('')
    setPromptExpanded(false)
  }, [])

  const handleClose = useCallback(() => {
    if (isDirty) {
      setShowDiscardConfirm(true)
      return
    }
    resetState()
    onClose()
  }, [isDirty, resetState, onClose])

  const handleConfirmDiscard = useCallback(() => {
    setShowDiscardConfirm(false)
    resetState()
    onClose()
  }, [resetState, onClose])

  const handleSaveChangesClick = useCallback(() => {
    if (formState) {
      if (formState.triggers.length === 0 || !formState.prompt.trim()) {
        setShowValidation(true)
        return
      }
    }

    setPhase('commit')
    setDialogState('pending')
  }, [formState])

  const handleCommitDialogStateChange = useCallback(
    (state: WebCommitDialogState) => {
      if (state === 'closed') {
        // User cancelled the commit dialog, go back to editing
        setPhase('edit')
        setDialogState('closed')
        setErrorMessage('')
        return
      }
      setDialogState(state)
    },
    [setPhase, setDialogState, setErrorMessage],
  )

  const handleSave = useCallback(async () => {
    setDialogState('saving')
    setErrorMessage('')

    try {
      const result = await saveConfig({
        content,
        message: commitMessage,
        description: commitDescription,
        commit_choice: effectiveIsQuickPull ? 'quick-pull' : 'direct',
        target_branch: effectiveIsQuickPull ? effectivePRTargetBranch : undefined,
        author_email: authorEmail,
        blob_oid: configData?.blob_oid,
      })

      setDialogState('saved')
      resetState()
      onSaved()

      // Navigate to the PR or redirect URL if one was returned
      const redirectUrl = result?.pull_request_url || result?.redirect_url
      if (redirectUrl) {
        window.location.href = redirectUrl
      }
    } catch (error) {
      setDialogState('pending')
      setErrorMessage(error instanceof Error ? error.message : 'Failed to save configuration')
    }
  }, [
    content,
    commitMessage,
    commitDescription,
    effectiveIsQuickPull,
    effectivePRTargetBranch,
    authorEmail,
    configData,
    saveConfig,
    resetState,
    onSaved,
  ])

  if (!isOpen) return null

  // Commit phase: show the WebCommitDialog
  if (phase === 'commit' && configData) {
    const {commit_info: commitInfo} = configData
    const webCommitInfo = buildWebCommitInfo(
      commitInfo.can_commit_status,
      commitInfo.author_emails,
      commitInfo.default_email,
      commitInfo.default_new_branch_name,
      commitInfo.commit_oid,
      commitInfo.dco_signoff_enabled,
    )

    const placeholderMessage = configData.exists ? 'Update triage agent configuration' : 'Configure triage agent'

    const fileStatuses: Record<string, 'A' | 'M'> = {
      [configData.config_path]: configData.exists ? 'M' : 'A',
    }

    return (
      <Suspense fallback={null}>
        <WebCommitDialog
          message={commitMessage}
          setMessage={setCommitMessage}
          description={commitDescription}
          setDescription={setCommitDescription}
          isQuickPull={effectiveIsQuickPull}
          setIsQuickPull={setIsQuickPull}
          prTargetBranch={effectivePRTargetBranch}
          setPRTargetBranch={setPRTargetBranch}
          errorMessage={errorMessage}
          dialogState={dialogState}
          setDialogState={handleCommitDialogStateChange}
          setAuthorEmail={setAuthorEmail}
          onSave={handleSave}
          refName={configData.default_branch}
          webCommitInfo={webCommitInfo}
          helpUrl="https://docs.github.com/articles/about-protected-branches"
          placeholderMessage={placeholderMessage}
          fileStatuses={fileStatuses}
          returnFocusRef={returnFocusRef}
        />
      </Suspense>
    )
  }

  // Edit phase: show the config editor
  return (
    <>
      <Dialog
        title="Configure triage agent"
        width="xlarge"
        onClose={handleClose}
        renderHeader={({dialogLabelId, onClose: onHeaderClose}) => (
          <Dialog.Header>
            <div className={styles.headerInner}>
              <div className={clsx(styles.headerContent, !promptExpanded && styles.headerContentDefault)}>
                {promptExpanded && (
                  <IconButton
                    icon={ArrowLeftIcon}
                    aria-label="Back to configuration"
                    variant="invisible"
                    size="small"
                    onClick={() => setPromptExpanded(false)}
                    data-testid="prompt-back-button"
                  />
                )}
                <Dialog.Title id={dialogLabelId}>
                  {promptExpanded ? 'Update prompt' : 'Configure triage agent'}
                </Dialog.Title>
              </div>
              <Dialog.CloseButton onClose={() => onHeaderClose('close-button')} />
            </div>
          </Dialog.Header>
        )}
        renderBody={() => (
          <div
            className={clsx(styles.dialogBody, promptExpanded && styles.dialogBodyExpanded)}
            data-testid="config-modal-body"
          >
            {isLoading && (
              <div className={styles.spinnerContainer}>
                <Spinner aria-label="Loading configuration" />
              </div>
            )}

            {isError && <Flash variant="danger">Failed to load triage agent configuration.</Flash>}

            {!isLoading && !isError && formState && (
              <>
                {hasInvalidConfig && configData && !promptExpanded && (
                  <Banner
                    variant="warning"
                    onDismiss={() => setHasInvalidConfig(false)}
                    title="Invalid front matter"
                    description={
                      <span>
                        The{' '}
                        <a
                          href={`/${repoOwner}/${repoName}/blob/${configData.default_branch}/${configData.config_path}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          previous configuration
                        </a>{' '}
                        had invalid front matter. We&apos;ve reset it to the default.
                      </span>
                    }
                  />
                )}

                {!promptExpanded && (
                  <TriggersSection
                    triggers={formState.triggers}
                    onChange={triggers => setFormState({...formState, triggers})}
                  />
                )}

                <PromptSection
                  prompt={formState.prompt}
                  onChange={prompt => setFormState({...formState, prompt})}
                  showValidation={showValidation}
                  isExpanded={promptExpanded}
                  onToggleExpand={() => setPromptExpanded(prev => !prev)}
                />

                {!promptExpanded && (
                  <ActionsSection
                    actions={formState.actions}
                    onChange={actions => setFormState({...formState, actions})}
                  />
                )}
              </>
            )}
          </div>
        )}
      >
        <Dialog.Footer className={styles.footerBar}>
          {configData && (
            <span className={styles.filePath}>
              Editing{' '}
              <a
                href={getTriageAgentConfigUrl({
                  repoOwner,
                  repoName,
                  defaultBranch: configData.default_branch,
                  configPath: configData.config_path,
                })}
                className={styles.configPath}
                target="_blank"
                rel="noreferrer"
              >
                {configData.config_path}
              </a>
            </span>
          )}
          <div className={styles.actionButtons}>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              ref={returnFocusRef}
              variant="primary"
              onClick={handleSaveChangesClick}
              disabled={isLoading || isError || !content.trim()}
              data-testid="save-changes-button"
            >
              Commit changes
            </Button>
          </div>
        </Dialog.Footer>
      </Dialog>

      {showDiscardConfirm && (
        <ConfirmationDialog
          title="Unsaved changes"
          onClose={gesture => {
            if (gesture === 'confirm') {
              handleConfirmDiscard()
            } else {
              setShowDiscardConfirm(false)
            }
          }}
          confirmButtonContent="Discard"
          confirmButtonType="danger"
          cancelButtonContent="Keep editing"
        >
          You have unsaved changes. Discard changes?
        </ConfirmationDialog>
      )}
    </>
  )
}
